// @flow
import React, { useState, useEffect } from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import styled from 'styled-components';
import useForm from './hooks/useForm';
import { screen } from './lib/utils';
import theme from './theme';

import Input, { StyledInput } from './components/Input';
import Checkbox, { StyledCheckbox } from './components/Checkbox';
import Button from './components/Button';

const CREATE_SUBMISSION = gql`
  mutation CREATE_SUBMISSION($input: CreatePromoSubmissionInput!) {
    submission: createPromoSubmission(input: $input) {
      id
      email
      promoCode
    }
  }
`;

const UPDATE_SUBMISSION = gql`
  mutation UPDATE_SUBMISSION($input: CreateMapSubmissionInput!) {
    submission: createMapSubmission(input: $input) {
      id
    }
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  ${StyledInput},
  ${StyledCheckbox} {
    margin: 10px 0;

    ${screen.atLeast.tablet`
      margin: 15px 0;
    `}
  }

  ${Button} {
    ${screen.smallerThan.tablet`
      width: 100%;
      text-align: center;
    `}
  }
`;

type FormPropsType = {
  afterSubmit: any,
};
export const Form = ({ afterSubmit }: FormPropsType) => {
  const initialFormData = {
    email: '',
    name: '',
    phone: '',
    // address: '', // honeypot
    agree: false,
  };
  const validate = (values) => {
    const errors = {};
    const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!values.email) {
      errors.email = 'This field must be filled!';
    } else if (!emailRegEx.test(values.email)) {
      errors.email = 'Incorrect email address!';
    }

    if (!values.name) {
      errors.name = 'This field must be filled!';
    }

    if (!values.agree) {
      errors.agree = 'Jums jāpiekrīt, ka Jūsu sniegtie dati tiks apkopoti!';
    }

    return errors;
  };
  const [hadSubmit, setHadSubmit] = useState(false);
  const { formData, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initialFormData,
    setHadSubmit,
    validate,
  );
  const [
    createPromoSubmission,
    { data, loading, error: serverError },
  ] = useMutation(CREATE_SUBMISSION);

  const submitData = async () => {
    const { name, email, phone } = formData;
    await createPromoSubmission({
      variables: {
        input: {
          clientMutationId: 'createPromoSubmission',
          name,
          email,
          phone,
        },
      },
    });
  };

  // After getting data
  useEffect(() => {
    if (data) afterSubmit(data);
  }, [data, afterSubmit]);

  // Servera kļūdu apstrāde
  useEffect(() => {
    if (serverError) {
      const { graphQLErrors } = serverError;
      if (graphQLErrors.length) {
        const [error] = graphQLErrors;
        if (/email/i.test(error.message)) {
          console.log('GQL E-pasta Errors:', error.message);
        }
      }
    }
  }, [serverError]);

  return (
    <StyledForm onSubmit={(ev) => handleSubmit(ev, submitData)}>
      <Input
        width={50}
        label="Framing shop (title)"
        name="name"
        disabled={loading}
        value={formData.name}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.name)}
        error={errors.name}
        hadSubmit={hadSubmit}
      />
      <Input
        width={50}
        label="E-mail address"
        name="email"
        disabled={loading}
        value={formData.email}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.email)}
        error={errors.email}
        hadSubmit={hadSubmit}
      />
      <Checkbox
        name="agree"
        checked={formData.agree}
        onChange={(ev) => handleChange(ev, true)}
      >
        I have read the{' '}
        <a
          href="/privacy-policy"
          target="_blank"
          style={{ textDecoration: 'underline' }}
        >
          privacy policy
        </a>
      </Checkbox>
      <div style={{ margin: `2rem 0` }}>
        {serverError && (
          <p
            style={{
              color: theme.default.colors.darkRed,
              margin: `0 0 1em`,
              fontSize: '1.125rem',
            }}
          >
            {serverError.message.replace('GraphQL error: ', '')}
          </p>
        )}
        <Button
          type="submit"
          className="button"
          style={{
            paddingLeft: 100,
            paddingRight: 100,
          }}
          disabled={loading || (hadSubmit && Object.keys(errors).length)}
        >
          {loading ? 'Please wait...' : 'Submit'}
        </Button>
      </div>
    </StyledForm>
  );
};

type MapFormPropsType = {
  id: number,
  afterSubmit: any,
};
export const MapForm = ({ id, afterSubmit }: MapFormPropsType) => {
  const initialFormData = {
    id,
    country: '',
    city: '',
    address: '',
    phone: '',
    homepage: '',
  };
  const validate = (values) => {
    const errors = {};

    if (!values.country) errors.country = 'This field must be filled!';
    if (!values.city) errors.city = 'This field must be filled!';
    if (!values.address) errors.address = 'This field must be filled!';

    return errors;
  };
  const [hadSubmit, setHadSubmit] = useState(false);
  const { formData, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initialFormData,
    setHadSubmit,
    validate,
  );
  const [
    createMapSubmission,
    { data, loading, error: serverError },
  ] = useMutation(UPDATE_SUBMISSION);

  const submitData = async () => {
    const { country, city, address, phone, homepage } = formData;
    await createMapSubmission({
      variables: {
        input: {
          clientMutationId: 'createMapSubmission',
          id,
          country,
          city,
          address,
          phone,
          homepage,
        },
      },
    });
  };

  useEffect(() => {
    if (data) afterSubmit(data);
  }, [data, afterSubmit]);

  // Servera kļūdu apstrāde
  useEffect(() => {
    if (serverError) {
      const { graphQLErrors } = serverError;
      if (graphQLErrors.length) {
        const [error] = graphQLErrors;
        if (/email/i.test(error.message)) {
          console.log('GQL E-pasta Errors:', error.message);
        }
      }
    }
  }, [serverError]);

  return data ? (
    <p>{JSON.stringify(data)}</p>
  ) : (
    <StyledForm onSubmit={(ev) => handleSubmit(ev, submitData)}>
      <Input
        width={50}
        label="Country* (Mandatory)"
        name="country"
        disabled={loading}
        value={formData.country}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.country)}
        error={errors.country}
        hadSubmit={hadSubmit}
      />
      <Input
        width={50}
        label="City* (Mandatory)"
        name="city"
        disabled={loading}
        value={formData.city}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.city)}
        error={errors.city}
        hadSubmit={hadSubmit}
      />
      <Input
        width={50}
        label="Address* (Mandatory)"
        name="address"
        disabled={loading}
        value={formData.address}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.address)}
        error={errors.address}
        hadSubmit={hadSubmit}
      />
      <Input
        width={50}
        label="Phone number (Optional)"
        name="phone"
        disabled={loading}
        value={formData.phone}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.phone)}
        error={errors.phone}
        hadSubmit={hadSubmit}
      />
      <Input
        width={50}
        label="Home page (Optional)"
        name="homepage"
        disabled={loading}
        value={formData.homepage}
        onBlur={handleBlur}
        onChange={(ev) => handleChange(ev, errors.homepage)}
        error={errors.homepage}
        hadSubmit={hadSubmit}
      />
      <div style={{ margin: `2rem 0` }}>
        {serverError && (
          <p
            style={{
              color: theme.default.colors.darkRed,
              margin: `0 0 1em`,
              fontSize: '1.125rem',
            }}
          >
            {serverError.message.replace('GraphQL error: ', '')}
          </p>
        )}
        <Button
          type="submit"
          className="button"
          style={{
            paddingLeft: 100,
            paddingRight: 100,
            marginBottom: '2rem',
          }}
          disabled={loading || (hadSubmit && Object.keys(errors).length)}
        >
          {loading ? 'Please wait...' : 'Submit'}
        </Button>
      </div>
    </StyledForm>
  );
};

export default Form;
